"use client"

import { useState } from "react"
import { Calculator, Copy, Printer, Share2 } from "lucide-react"

type Result = {
   pct: number
   perDollar: number
   foodCost: number
   revenue: number
   statusLabel: string
   statusColor: string
   statusNote: string
}

function getStatus(pct: number): { label: string; color: string; note: string } {
   if (pct < 10) return { label: "Verify", color: "#c77c00", note: "Numbers look unusual — double-check your inputs." }
   if (pct <= 27.99) return { label: "Excellent", color: "#1a7a3c", note: "Strong margins. Keep up the good work." }
   if (pct <= 35) return { label: "Good", color: "#1e5fa8", note: "Within the healthy range for most restaurants." }
   return { label: "High", color: "#C8102E", note: "Review supplier costs, portioning, and waste." }
}

// function fmt(n: number) {
//    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
// }

const prefixInput =
   "w-full rounded border border-gray-200 py-3 pl-8 pr-3 text-base text-gray-900 outline-none transition focus:border-[#EB5A23] focus:ring-2 focus:ring-[#EB5A23]/10"

export default function FoodCostCalculatorCard() {
   const [foodCost, setFoodCost] = useState("")
   const [revenue, setRevenue] = useState("")
   const [result, setResult] = useState<Result | null>(null)
   const [error, setError] = useState<string | null>(null)
   const [resultCopied, setResultCopied] = useState(false)
   const [shareCopied, setShareCopied] = useState(false)

   function calculate() {
      setError(null)
      const cost = parseFloat(foodCost)
      const rev = parseFloat(revenue)

      if (isNaN(cost) || isNaN(rev) || rev <= 0 || cost < 0) {
         setError("Please enter valid positive numbers for both fields.")
         return
      }

      const pct = (cost / rev) * 100
      const perDollar = cost / rev
      const s = getStatus(pct)

      setResult({
         pct,
         perDollar,
         foodCost: cost,
         revenue: rev,
         statusLabel: s.label,
         statusColor: s.color,
         statusNote: s.note,
      })
   }

   function reset() {
      setFoodCost("")
      setRevenue("")
      setResult(null)
      setError(null)
   }

   function copyResult() {
      if (!result) return
      navigator.clipboard
         .writeText(
            `Food Cost Calculator — restaurantassociation.com\nFood Cost %: ${result.pct.toFixed(2)}%\nCost per $1 Revenue: $${result.perDollar.toFixed(2)}\nHealth Status: ${result.statusLabel}`,
         )
         .catch(() => null)
      setResultCopied(true)
      setTimeout(() => setResultCopied(false), 2000)
   }

   async function shareLink() {
      const url = window.location.href
      if (navigator.share) {
         try {
            await navigator.share({ title: "Food Cost Calculator", text: "Calculate your food cost percentage.", url })
            return
         } catch {
            // cancelled or unsupported — fall through
         }
      }
      await navigator.clipboard.writeText(url).catch(() => null)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
   }

   return (
      <>
         <div
            className="mb-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
            style={{ borderTop: "3px solid #EB5A23" }}
         >
            <div className="px-7 pt-7">
               <h2 className="mb-1 text-custom-3xl-bold">Enter Your Numbers</h2>
               <p className="mb-6 text-custom-lg-normal text-gray-400">
                  Enter your total food cost and revenue for any period — daily, weekly, or monthly.
               </p>
            </div>

            <div className="px-7 pb-7">
               <div className="mb-4">
                  <label className="mb-1.5 block text-custom-base-normal uppercase text-gray-700">
                     Total Food Cost <span className="text-[#EB5A23]">*</span>
                  </label>
                  <p className="mb-2 text-custom-sm-normal text-gray-400">
                     Total spent on ingredients and raw materials
                  </p>
                  <div className="relative">
                     <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                        $
                     </span>
                     <input
                        type="number"
                        min={0}
                        step="any"
                        value={foodCost}
                        onChange={(e) => setFoodCost(e.target.value)}
                        placeholder="e.g. 50000"
                        className={prefixInput}
                     />
                  </div>
               </div>

               <div className="mb-6">
                  <label className="mb-1.5 block text-custom-base-normal uppercase  text-gray-700">
                     Total Revenue <span className="text-[#EB5A23]">*</span>
                  </label>
                  <p className="mb-2 text-custom-sm-normal text-gray-400">Total food sales for the same period</p>
                  <div className="relative">
                     <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                        $
                     </span>
                     <input
                        type="number"
                        min={0}
                        step="any"
                        value={revenue}
                        onChange={(e) => setRevenue(e.target.value)}
                        placeholder="e.g. 180000"
                        className={prefixInput}
                     />
                  </div>
               </div>

               <button
                  onClick={calculate}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#EB5A23] py-4 text-custom-sm-bold uppercase tracking-wider text-white transition hover:bg-[#cf4e1e] active:scale-[0.99]"
               >
                  <Calculator className="h-5 w-5" />
                  Calculate Food Cost %
               </button>
               <button
                  onClick={reset}
                  className="mt-3 w-full rounded-xl border border-gray-200 py-2.5 text-custom-sm-bold  text-gray-600 transition hover:border-[#EB5A23] hover:text-[#EB5A23]"
               >
                  ↺ Reset Calculator
               </button>

               {error && (
                  <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-600">
                     ⚠ {error}
                  </div>
               )}

               {result ? (
                  <div className="mt-4 rounded-xl border-2 bg-white p-6" style={{ borderColor: result.statusColor }}>
                     <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">
                        Your Food Cost Percentage
                     </p>
                     <p className="mb-5 font-serif text-5xl font-bold" style={{ color: result.statusColor }}>
                        {result.pct.toFixed(2)}%
                     </p>

                     <div className="overflow-hidden rounded-lg border border-gray-100">
                        <div className="grid grid-cols-2 divide-x divide-gray-100">
                           <div className="px-4 py-4 text-center">
                              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                                 Per $1 Revenue
                              </p>
                              <p className="font-serif text-xl font-bold text-gray-900">
                                 ${result.perDollar.toFixed(2)}
                              </p>
                              <p className="mt-1 text-xs text-gray-400">cost per dollar earned</p>
                           </div>
                           <div className="px-4 py-4 text-center">
                              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                                 Health Status
                              </p>
                              <span
                                 className="mt-1 inline-block rounded-full px-3 py-1 text-sm font-bold"
                                 style={{
                                    color: result.statusColor,
                                    backgroundColor: result.statusColor + "18",
                                    border: `1px solid ${result.statusColor}30`,
                                 }}
                              >
                                 {result.statusLabel}
                              </span>
                              <p className="mt-2 text-xs text-gray-400">{result.statusNote}</p>
                           </div>
                        </div>
                     </div>
                  </div>
               ) : (
                  !error && (
                     <div className="mt-4 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-100 bg-gray-50 py-10 text-center text-gray-400">
                        <svg
                           className="h-10 w-10 opacity-30"
                           viewBox="0 0 24 24"
                           fill="none"
                           stroke="currentColor"
                           strokeWidth={1.5}
                        >
                           <path
                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           />
                        </svg>
                        <p className="text-custom-base-bold text-gray-500">Ready to calculate</p>
                        <p className="text-custom-sm-semibold">Enter your values above to see your results.</p>
                     </div>
                  )
               )}
            </div>
         </div>

         <div className="mb-10 grid grid-cols-3 gap-3">
            {[
               {
                  label: resultCopied ? "Copied!" : "Copy Result",
                  icon: <Copy className="h-4 w-4" />,
                  onClick: copyResult,
               },
               {
                  label: shareCopied ? "Link Copied!" : "Share Link",
                  icon: <Share2 className="h-4 w-4" />,
                  onClick: shareLink,
               },
               { label: "Print", icon: <Printer className="h-4 w-4" />, onClick: () => window.print() },
            ].map(({ label, icon, onClick }) => (
               <button
                  key={label}
                  onClick={onClick}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:border-[#EB5A23] hover:bg-orange-50 hover:text-[#EB5A23]"
               >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
               </button>
            ))}
         </div>
      </>
   )
}
